/**
 * Pocket Radar CSV → canonical throw objects.
 *
 * One source of truth for: column names, type coercion, timestamp parsing
 * (UTC), and Activity-string → throw_context enum mapping.
 *
 * Pocket Radar's export is much simpler than Rapsodo's — 10 columns vs 33,
 * one row per throw, no tracking-failure rows to filter. The CSV exports
 * include Player Name, so we auto-link to athletes the same way Rapsodo
 * does (exact First+Last match).
 *
 * Schema sourced from a real export (Dean Smallwood, 1/15/2026 pen lesson);
 * if Pocket Radar ever changes their headers, this is the only file to update.
 */

import Papa from 'papaparse';

// ============================================================
// Column map: PR CSV header → canonical (snake_case) field name.
// 10 columns total in the standard export.
// ============================================================
export const POCKET_RADAR_COLUMNS = {
  'Date':           'date_str',          // "1/15/26"
  'Time':           'time_str',          // "5:56:22 PM"
  'Session Title':  'session_title',     // free-form coach text
  'Count':          'throw_num',         // 1, 2, 3, ...
  'Speed':          'velo_mph',          // integer mph (or kph if Unit=KPH)
  'Unit':           'unit',              // "MPH" | "KPH"
  'Player Name':    'player_name_full',  // "First Last"
  'Sport':          'sport',             // "Baseball"
  'Activity':       'activity_str',      // "Pitching - Bullpen"
  'Video':          'video_flag',        // "YES" | "NO"
};

// ============================================================
// Activity string → canonical throw_context enum.
// Maps to the same 6-value vocabulary the original plan called for
// (warmup / drill / long_toss / mound / bullpen / other).
// Pass-through to 'other' for any unknown Activity string.
// ============================================================
const ACTIVITY_TO_CONTEXT = {
  'Pitching - Bullpen':       'bullpen',
  'Pitching - Mound':         'mound',
  'Pitching - Long Toss':     'long_toss',
  'Pitching - Drill':         'drill',
  'Pitching - Warmup':        'warmup',
  'Pitching - Warm Up':       'warmup',
  'Throwing - Long Toss':     'long_toss',
  'Throwing - Warmup':        'warmup',
  'Throwing - Warm Up':       'warmup',
  'Throwing - Drill':         'drill',
};

export function activityToContext(activity) {
  if (!activity || typeof activity !== 'string') return 'other';
  return ACTIVITY_TO_CONTEXT[activity.trim()] ?? 'other';
}

// ============================================================
// extractFilenameKey: PR filename → dedup key (the filename basename
// without extension). PR exports name files like:
//   PR_20260115_1753_10_pitch_lesson_pen.csv
// We use the full basename as the natural unique key on
// pocket_radar_uploads. Coaches who name two distinct sessions identically
// are responsible for renaming.
// ============================================================
// Content sniffer — recognize Pocket Radar exports even if the coach
// renamed the file. PR's signature: "Session Title" + "Player Name"
// columns in the header row. Both present → PR.
export function isPocketRadarCsv(csvText) {
  if (!csvText || typeof csvText !== 'string') return false;
  const firstLine = csvText.split(/\r?\n/, 1)[0] ?? '';
  return firstLine.includes('Session Title') && firstLine.includes('Player Name');
}

export function extractFilenameKey(filenameOrPath) {
  if (!filenameOrPath || typeof filenameOrPath !== 'string') return null;
  // Strip directories.
  const base = filenameOrPath.split(/[\/\\]/).pop();
  // Strip extension.
  const noExt = base.replace(/\.[^./\\]+$/, '');
  // PR exports start with "PR_" — use that as a sanity check.
  if (!/^PR_/i.test(noExt)) return null;
  return noExt;
}

// ============================================================
// parsePocketRadarTimestamp: ("1/15/26", "5:56:22 PM") → UTC ISO.
// PR records local-time on the iPad. Without a tz column, we assume
// America/Chicago (TAP's facility tz) and convert to UTC.
//
// We don't use the JS Date constructor on combined strings because of
// browser/locale ambiguity — instead, we parse explicitly and build the
// timestamp as UTC offset by Chicago's offset on that date.
// ============================================================
export function parsePocketRadarTimestamp(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;

  // "1/15/26" → [1, 15, 26]
  const dateMatch = String(dateStr).match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (!dateMatch) return null;
  let [, mm, dd, yy] = dateMatch.map((s, i) => i === 0 ? s : parseInt(s, 10));
  const month = mm;
  const day = dd;
  const year = yy < 100 ? 2000 + yy : yy;

  // "5:56:22 PM" → [5, 56, 22, "PM"]
  const timeMatch = String(timeStr).match(/^(\d{1,2}):(\d{2}):(\d{2})\s*(AM|PM)?$/i);
  if (!timeMatch) return null;
  let [, hStr, miStr, sStr, ampm] = timeMatch;
  let hour = parseInt(hStr, 10);
  const minute = parseInt(miStr, 10);
  const second = parseInt(sStr, 10);
  if (ampm) {
    const upper = ampm.toUpperCase();
    if (upper === 'PM' && hour < 12) hour += 12;
    if (upper === 'AM' && hour === 12) hour = 0;
  }

  // Build a UTC timestamp by computing what UTC time corresponds to the
  // local Chicago wall-clock time. We do this by formatting a candidate
  // UTC time as Chicago time and adjusting by the difference. This handles
  // DST transitions correctly without hardcoding offsets.
  return localChicagoToUtc(year, month, day, hour, minute, second);
}

function localChicagoToUtc(year, month, day, hour, minute, second) {
  // First-pass guess: treat the local time as if it were UTC.
  const guess = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  // Now ask Intl what time *that UTC moment* shows in Chicago.
  const chicagoStr = guess.toLocaleString('en-US', {
    timeZone: 'America/Chicago',
    hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  // Parse it back. en-US locale gives "MM/DD/YYYY, HH:MM:SS".
  const m = chicagoStr.match(/(\d{2})\/(\d{2})\/(\d{4}),?\s+(\d{2}):(\d{2}):(\d{2})/);
  if (!m) return null;
  const [, cmm, cdd, cyyyy, ch, cmi, cs] = m.map((x) => parseInt(x, 10));
  // Difference between guess (interpreted as Chicago) and our intended local
  // time gives us the UTC adjustment.
  const guessAsLocal = Date.UTC(cyyyy, cmm - 1, cdd, ch, cmi, cs);
  const intendedAsLocal = Date.UTC(year, month - 1, day, hour, minute, second);
  const offsetMs = intendedAsLocal - guessAsLocal;
  return new Date(guess.getTime() + offsetMs).toISOString();
}

// ============================================================
// splitPlayerName: "First Last" → { first, last }
// PR exports the full name as one field. We split on the LAST space so
// multi-word first names (e.g. "Mary Beth Smith") still work — first =
// "Mary Beth", last = "Smith".
// ============================================================
export function splitPlayerName(fullName) {
  if (!fullName || typeof fullName !== 'string') return null;
  const trimmed = fullName.trim();
  if (!trimmed) return null;
  const lastSpace = trimmed.lastIndexOf(' ');
  if (lastSpace === -1) return { first: trimmed, last: '' };
  return {
    first: trimmed.slice(0, lastSpace).trim(),
    last: trimmed.slice(lastSpace + 1).trim(),
  };
}

// ============================================================
// normalizeThrow: one PR CSV row → canonical throw object.
// Preserves the original row under .raw for forward-compat.
// ============================================================
export function normalizeThrow(rawRow) {
  const t = { raw: rawRow };
  for (const [csvKey, canonicalKey] of Object.entries(POCKET_RADAR_COLUMNS)) {
    t[canonicalKey] = rawRow[csvKey] ?? null;
  }
  // Coerce numerics.
  t.throw_num = toInt(t.throw_num);
  t.velo_mph = toMph(t.velo_mph, t.unit);
  t.video_flag = t.video_flag === 'YES' || t.video_flag === 'yes' || t.video_flag === true;
  // Build canonical timestamp.
  t.thrown_at = parsePocketRadarTimestamp(t.date_str, t.time_str);
  // Map Activity → throw_context enum.
  t.throw_context = activityToContext(t.activity_str);
  // Split player name.
  const split = splitPlayerName(t.player_name_full);
  t.pitcher_first = split?.first ?? null;
  t.pitcher_last = split?.last ?? null;

  return t;
}

function toInt(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
}

function toMph(speed, unit) {
  if (speed === null || speed === undefined || speed === '') return null;
  const n = parseFloat(speed);
  if (!Number.isFinite(n)) return null;
  if (typeof unit === 'string' && unit.toUpperCase() === 'KPH') {
    return Math.round(n * 0.621371 * 10) / 10;
  }
  return n;
}

// ============================================================
// isValidThrow: filter rows that aren't real throws.
// PR doesn't emit the kind of all-zeros placeholder rows Rapsodo does,
// but we still guard against missing speed or missing timestamp.
// ============================================================
export function isValidThrow(t) {
  if (!t) return false;
  return Number.isFinite(t.velo_mph) && t.velo_mph > 0 && t.thrown_at != null;
}

// ============================================================
// parsePocketRadarCsv: full pipeline, CSV text → normalized + filtered + sorted.
// Returns { throws, filteredCount, unknownColumns, playerName }.
// throws are sorted by thrown_at ascending.
// playerName is the unique Player Name across all valid throws (or null if
// the CSV mixes athletes — flagged as a single-athlete-only constraint).
// ============================================================
export function parsePocketRadarCsv(csvText) {
  const result = Papa.parse(csvText, {
    header: true,
    skipEmptyLines: true,
  });

  const throws = [];
  let filteredCount = 0;

  for (const row of result.data) {
    const normalized = normalizeThrow(row);
    if (isValidThrow(normalized)) {
      throws.push(normalized);
    } else {
      filteredCount++;
    }
  }

  throws.sort((a, b) => a.thrown_at.localeCompare(b.thrown_at));

  const knownColumns = new Set(Object.keys(POCKET_RADAR_COLUMNS));
  const csvHeaders = result.meta?.fields ?? [];
  const unknownColumns = csvHeaders.filter((h) => !knownColumns.has(h));

  // Single-athlete check: all throws must share the same Player Name.
  const names = new Set(throws.map((t) => t.player_name_full).filter(Boolean));
  const playerName = names.size === 1 ? [...names][0] : null;

  return { throws, filteredCount, unknownColumns, playerName };
}
